import "reflect-metadata";
import { AuthService } from "../app/core-services/auth.service";
import { StorageService } from "../app/core-services/storage.service";
import { DBOnSnapShotFnType, DatabaseService, DBQueryWhereIn, WhereParams } from "../app/core-services/database.service";
import { withCtx } from "./context";

// const parentMetadataKey = Symbol("parent");

export type QueryOpt<T> = {
    parentName: string; 
    offset?: string | number; 
    orderBy?: any, 
    limit?: number,
    whereEQ?: WhereParams | WhereParams[];
    whereGT?: {
      includeEqual?: boolean;
      column: string;
      value: string | number;
    };
    whereLT?: {
      includeEqual?: boolean;
      column: string;
      value: string | number;
    };
    whereIN?: DBQueryWhereIn<T>;
    whereIncludes?: {
        column: string;
        value: string | number;
    };
  };
export abstract class BaseEntity<C extends BaseEntity<C>> {
    public id: string;
    public version: string;
    public deleted: boolean;
    public pendingSync: boolean = false;
    public _localStored: boolean = true;
    public get localStored(): boolean {
      return this._localStored;
    }
    protected _name: string;
    protected _storagePersist: boolean;
    protected _databasePersist: boolean;
    protected _parent;
    protected _index: Map<string, any> = new Map();
    private __storageItems: Map<string, any> = new Map();
    private set _storageItems(value) {
        this.__storageItems = value;
    }
    private get _storageItems() {
        return this.constructor.prototype.__storageItems;
    }
    protected _loaded: boolean;

    public get loaded() {
        return this._loaded;
    }

    static get canonical() {
        return this.prototype._name || this.constructor.name;
    }

    public get canonical() {
        return this._name || this.constructor.name;
    }

    constructor() {
        if (this._parent?.default) {
            this.loadParentDefault().then();
        }
    }
    
    protected async loadParentDefault() {
        this._parent.value = await this._parent.default();
        return true;
    }

    abstract export<T>(): T;

    private async updateIndexes() {
        const _index = this.constructor.prototype._index || this._index;
    
        if (!_index) return;
        for (const [key, value] of _index?.entries()) {
            await DatabaseService.instance.set(`IDX_${this.canonical.toUpperCase()}_${key.toUpperCase()}/${this._parent.value}:${value?.toUpperCase()}`, {
                id: this.id,
                parent: this._parent.value,
            });
        }
        // TODO: Delete old indexes
        
    }

    public async postLoad() {
        return this;
    }

    public async persist(onSnapshot?: DBOnSnapShotFnType) {
        return await withCtx({}, async () => {
            const data = this.export() as any;

            if (this._databasePersist) {
                const canSync = await AuthService.instance?.canWrite(this.canonical);
                this.pendingSync = !canSync;
                DatabaseService.instance.pendingSync = !canSync;
         
                // try {
                    // if (canSync) {
                        await DatabaseService.instance.set(this._key, data, {
                            merge: true,
                            onSnapshot
                        });
                        await this.updateIndexes();

                        AuthService.instance.doWrite(this.canonical);
                        this._localStored = false;
                    // }
                // } catch(e) {
                //     console.log(e)
                //     this.pendingSync = true;
                //     DatabaseService.instance.pendingSync = true;
                // }
            }
            
            await this.storagePersist(); 

            const promises = [];
            let store = false;
            if (!this._storageItems) return this;
            for (const [key, value] of this._storageItems?.entries()) {
                if (data[key] && this[key]) {
                    promises.push(StorageService.instance.setItem(key, this[key]));
                    store = true;   
                }
            }

            if (store) {
                await Promise.all(promises);
            }
            return this;
        });
    }

    public async delete() {
        return await withCtx({}, async () => {
            const canSync = await AuthService.instance?.isLogged;
            let pendingSync = false;

            if (!this._parent?.value && this._parent?.default) {
                await this.loadParentDefault();
            }
 

            if (canSync) {
                try {
                    await DatabaseService.instance.delete(this._key);
                    AuthService.instance.doWrite(this.canonical, -1);
                } catch(e) {
                    console.log(e);
                    pendingSync = true;
                }
            }
            
            if (!this._parent?.name) {
                // TODO
            } else {
                await StorageService.instance.deleteCacheItem(this._name, this.id, pendingSync || !canSync);
            }
            
        });
    }
    public async storagePersist() {
        if (this._storagePersist) {
            const data = this.export() as any;
            const storageData = { ...data };
            for (const key of Object.keys(storageData)) {
                if (this._storageItems?.has(key)) {
                    delete storageData[key];
                }
            }
            if (!this._parent?.name) {
                await StorageService.instance.setItem(this._name, {...storageData, pendingSync: this.pendingSync});
            } else {
                await StorageService.instance.setCacheItem(this._name, this.id, {...storageData, pendingSync: this.pendingSync});
            }
        }
    }

    private static async loadStorageItems<C extends BaseEntity<C>>(target?: C) {
        target = target || this as any;
        if (!target._storageItems) return target;
        for (const [key, value] of target._storageItems?.entries()) {
            target[key] = await StorageService.instance.getItem(key, value);
        }
        return target;
    }

    protected get _key() {
        // const parent = Reflect.getMetadata(parentMetadataKey, this);
        const parent = (this._parent?.name && this._parent.value) ? this._parent : null;
        return `${(parent ? `${parent.name}/${parent.value}/` : "")}${this._name || this.constructor.name}/${this.id}`;
    }

    public static indexKey<C extends BaseEntity<C>>(key: keyof C, value: any) {
        const parent = this.constructor.prototype._parent || this.prototype._parent || this['_parent'];
        return `IDX_${this.canonical.toUpperCase()}_${key.toString().toUpperCase()}/${parent.value}:${value.toUpperCase()}`;
    }
    

    public async load<T = this>() {
       if (AuthService.instance?.canRead) {
        const data = await DatabaseService.instance.get<T>(this._key);
        if (data) {
            for (const attr of Object.keys(data)) {
                this[attr] = data[attr];
            }
            this._loaded = true;
        } else {

        }
       }

       if (this._storagePersist) {
        const data = await StorageService.instance.getCacheItem<C>(this._name || this.constructor.name, this.id);
        if (data.version > this.version) {
            for (const attr of Object.keys(data)) {
                this[attr] = data[attr];
            }
        }
       }

       return this;
    }

    static async findIndex<C extends BaseEntity<C>>(key: keyof C, value: any): Promise<string> {
        if (AuthService.instance?.canRead && value) {
            const parent = this.prototype._parent;
            const data = await DatabaseService.instance.get<{ id: string; parent: string; }>(this.indexKey(key, value)) as any;
            return data?.id;
        }
        return;
    }
    
    static async loadWhere<C extends BaseEntity<C>>(key: keyof C, value: any): Promise<C> {
        if (AuthService.instance?.canRead) {
            const parent = this.prototype._parent;
            const data = await DatabaseService.instance.get<C>(this.indexKey(key, value)) as C;             
            if (data) {
                return this.load(data.id);
            }
        }
        return;
    }

    static async load<C extends BaseEntity<C>>(id: string): Promise<C> {
        let data, database;
        if (AuthService.instance?.canRead) {
            const parent = this.prototype._parent;
            const key = `${(parent.name && (parent.value || parent.default) ? `${parent.name}/${parent.value || (typeof parent.default == 'function' ? parent.default.call(this) : parent.default)}/` : "")}${this.canonical}/${id}`;
            console.log("load", key, parent)
            data = await DatabaseService.instance.get<C>(key) as C; 
            if (data) database = true;             
        }

        if (this.prototype._storagePersist) {
            const storageData = await StorageService.instance.getCacheItem<C>(this.canonical, id);
            if (storageData && (!data || data.version > storageData.version)) {
                data = storageData;
            }
        }

        if (!data) {
            return null;
        }

        let instance = new (this as any as {new(): C})();
        data._localStored = !database;
        instance = Object.assign(instance, data);
        
        const result = await this.loadStorageItems(instance);
        
        result.postLoad();

        return result;

    }

    static async loadFromStorage<C extends BaseEntity<C>>(defaultValue?: C): Promise<C> {
        let storageData = await StorageService.instance.getCacheItem<C>(this.canonical); // || defaultValue;
        
        if (!storageData && !defaultValue) {
            return null;
        } else if (!!storageData) {
            storageData = (Object as any).fromEntries(storageData);
        }

        storageData = storageData || defaultValue || {} as any;

        let instance = new (this as any as {new(): C})();

     
        for (const [key, value] of this.prototype._storageItems?.entries() || []) {
            // if (!!instance[key]) {
                storageData[key] = await StorageService.instance.getItem(key, this[key]);
            // }
        }

        
        return Object.assign(instance, storageData);
    }


    static async loadAll<C extends BaseEntity<C>>(id: string, {parentName, offset, ignoreStorageDbData, orderBy}: {parentName: string; offset?: string; ignoreStorageDbData?: boolean; orderBy?: { column: keyof C; sort: 'asc' | 'desc'}} = {parentName: this.prototype._parent?.name}): Promise<C[]> {

        const dbdata = await this.loadAllFromDatabase<C>(id, {parentName, offset, orderBy});
        const storageData = await this.loadAllFromStorage<C>(true, ignoreStorageDbData);
        const mergedData = [...storageData, ...dbdata];
        const result: { [key: string]: C } = {};
        const deleteData: { [key: string]: C } = {};
        for (const o of mergedData) {
            if (result[o.id] && (result[o.id].version > o.version || deleteData[o.id])) {
                continue;
            }
            o._parent = {
                ...(o._parent || {}),
                name: parentName,
                value: id
            }
            if (o.deleted) {
                deleteData[o.id] = o;
                delete result[o.id];
            } else {
                result[o.id] = o;
                result[o.id].postLoad();
            }
        }

        if (AuthService.instance.isLogged && Object.values(deleteData).length > 0) {
            await DatabaseService.instance.withTx(async () => {
                for (const o of Object.values(deleteData)) {
                    await o.delete();
                }
            });
        }

        return Object.values(result);
    }

    static async loadAllFromDatabase<C extends BaseEntity<C>>(
        id: string, 
        {parentName, offset, orderBy, limit, whereEQ, whereGT, whereLT, whereIN, whereIncludes}: QueryOpt<C> = {
            parentName: this.prototype._parent?.name
        }): Promise<C[]> {

        const result: { [key: string]: C } = {};
        if (AuthService.instance?.canRead) {
            const data = (await DatabaseService.instance.get<C>(`${parentName}/${id}/${this.canonical}`, { startAfter: offset, orderBy, limit, whereEQ, whereGT, whereLT, whereIN, whereIncludes }) || []) as C[];
            
            for (const o of data) {
                const instance = new (this as any as {new(): C})();
                if (o['pendingSync']) {
                    o['pendingSync'] = false;
                }
                o['_localStored'] = false;
                result[o['id']] = Object.assign(instance, o);
                result[o['id']].postLoad();
            }
        }

        return Object.values(result);
    }
    
    static async loadAllFromStorage<C extends BaseEntity<C>>(setCache: boolean = true, ignoreStorageDbData?: boolean): Promise<C[]> {
        const result: { [key: string]: C } = {};

        if (this.prototype._storagePersist) {
            const data = await StorageService.instance.getItem<C[]>(this.canonical, []);
            if (setCache) {
                await StorageService.instance.setCacheItems(this.canonical, data);
            }
            for (const k in data) {
                const o = data[k];
                const instance = new (this as any as {new(): C})();
                if (!ignoreStorageDbData || !!o['_localStored'] || !!o['pendingSync']) {
                  result[o.id] = Object.assign(instance, o);
                  result[o.id].postLoad();
                }
            }
        }
        return Object.values(result);
    }

    static async persistMany<C>(data: C[], id: string, parentName: string = this.prototype._parent?.name): Promise<boolean> {
      if (this.prototype._databasePersist) {
        const key = `${parentName}/${id}/${this.canonical}`;
        try {
            if (AuthService.instance?.canRead) {
                await DatabaseService.instance.set(key, data);
                AuthService.instance.doWrite(this.canonical, data.length);
                return true;
            }
        } catch(e) {
            return false;
        }
      }
    }

    static async delete(id: string) {
        const parent = this.prototype._parent;
        const key = `${parent.name}/${parent.value}/${this.canonical}/${id}`;
        return await DatabaseService.instance.withTx(async () => {
           if (AuthService.instance?.isLogged) {
                // delete indexes
                const _index = this.prototype._index;
                if (_index) {
                    for (const [key, value] of _index?.entries()) {
                        await DatabaseService.instance.delete(this.indexKey(key as any, value));
                    }
                }
               return await DatabaseService.instance.delete(key);
           }
        });
    }

    static async deleteIndex<C extends BaseEntity<C>>(key: keyof C, value: any): Promise<boolean> {
        if (AuthService.instance?.canRead) {
            return await DatabaseService.instance.delete(this.indexKey(key as any, value));
        }
        return false;
    }

    static setParent(name: string, value: string) {
        this.prototype["_parent"] = {
            name,
            value
        }; 
    }
    
    static from<C extends BaseEntity<C>>(data: Partial<C>) {
        const instance = new (this as any as {new(): C})();
        return Object.assign(instance, data);
    }

    static shortFields(): any {
        throw new Error(`ShortFields not implemented on ${this.constructor.name}`);
    }

    static normalizeShortTrait<T, C extends BaseEntity<C>>(this: { shortFields: () => ShortFieldType<T, C> }, data: T): Partial<C> {
        const result: any = {};
        const fields = this.shortFields();
        for (const [key, value] of Object.entries(data)) {
            if (fields[key]) {
                result[fields[key]] = value;
            } else {
                result[key] = value;
            }
        }
        return result;
    }

    static fromShortFields<T, C extends BaseEntity<C>>(this: { 
        normalizeShortTrait: (d: T) => Partial<C>;
        from: (d: Partial<C>) => C;
    }, data: T): Partial<C> {
        return this.from(this.normalizeShortTrait(data));
    }
}



export type ShortFieldType<C,T> = {
    [K in keyof C]: keyof T;
};
  
type ModelEntityOptions<T extends BaseEntity<T> = any> = {
    name?: string;
    storagePersist?: boolean; 
    databasePersist?: boolean;
    parentName?: string;
    defaultValue?: () => any;
    propagateCollection?: {
        name: (this: T) => string | string[];
        exportFn?: (this: T, type?: 'union' | 'remove') => Partial<T> | any
    }[];
};

export function ModelEntity<T extends BaseEntity<T> = any>(
    value?: ModelEntityOptions<T> | string
    ) { 
    return function (constructor: Function) { 
      let data: ModelEntityOptions<T> = {};
      if (typeof value == 'string') {
        data.name = value;
      } else {
        data = value;
      }


      data.storagePersist = !!data.storagePersist;
      data.databasePersist = data.databasePersist == undefined ? true : data.databasePersist;
      data.parentName = data.parentName == undefined ? "users" : data.parentName;
      data.defaultValue = data.defaultValue == undefined ? () => AuthService.instance?.currentUser?.uid : data.defaultValue;

      constructor.prototype._name = data.name || constructor.prototype.name;
      constructor.prototype._storagePersist = data.storagePersist;
      constructor.prototype._databasePersist = data.databasePersist;
      constructor.prototype._parent = {
        name: data.parentName,
        value: null,
        default: data.defaultValue 
      }

      if (data.propagateCollection) {
        const originalPersist = constructor.prototype.persist;
        constructor.prototype.persist = async function(onSnapshot?: DBOnSnapShotFnType) {
  
              const result = await originalPersist.apply(this, [onSnapshot]);
              if (this._databasePersist) {
                  for (const c of data.propagateCollection) {
                      let path = c.name.apply(this);
                      if (typeof path == 'string') {
                            path = [path];
                      }
                    
                      for (const p of path) {
                        const _data = c.exportFn ? c.exportFn.apply(this, ['union',p]) : this.export();

                        await DatabaseService.instance.set(p, _data, {
                            merge: true,
                            onSnapshot
                        });
                      }
                  }
              }
              return result;
        }

        const originalDelete = constructor.prototype.delete;
        constructor.prototype.delete = async function() {
            const result = await originalDelete.apply(this);
            if (this._databasePersist) {
                for (const c of data.propagateCollection) {
                    if (!c.exportFn) continue;
                    let path = c.name.apply(this);
                    if (typeof path == 'string') {
                          path = [path];
                    }
                  
                    for (const p of path) {
                        const _data = c.exportFn.apply(this, ['remove', p]);
                        await DatabaseService.instance.set(p, _data, {
                            merge: true
                        });
                    }
                }
            }
            return result;
        }


      }
    }
}


export function ParentEntity(name?: string, defaultValue?: () => any | Promise<any>) {
    return function(target: Object, propertyKey: string) {
        
        Object.defineProperty(target, propertyKey, {
            get: () => target.constructor.prototype._parent?.value,
            set: (value) => {
                target.constructor.prototype._parent = {
                    name: name || propertyKey,
                    value,
                    default: defaultValue  
                }
            }
          });
    }
}

export function StoragePersist(data?: { name?: string; default?: any }) {
    return function(target: Object, propertyKey: string) {
        const _field = data?.name || propertyKey;

        if (!target.constructor.prototype._storageItems) {
            target.constructor.prototype._storageItems = new Map();
        }

        target.constructor.prototype._storageItems?.set(_field, data.default);
        Object.defineProperty(target, propertyKey, {
            get: () => target.constructor.prototype._storageItems?.get(_field) || data.default,
            set: (value) => {
                target.constructor.prototype._storageItems?.set(_field, value);
            }
        });
    }
}

export function EntityIndex({ name, default: defaultValue }: { name?: string; default?: any } = {}) {
    return function(target: Object, propertyKey: string) {
        if (Reflect.deleteProperty(target, propertyKey)) {
            Object.defineProperty(target, propertyKey, {
                get: function() { return  this[`_${propertyKey}`] },
                set: function(value) {
                    this[`_${propertyKey}`] = value;

                    if (!this._index) {
                        this._index = new Map();
                    }

                    this._index.set(name || propertyKey, value || (typeof defaultValue == 'function' ? defaultValue.apply(this) : defaultValue));
                },
                enumerable: true,
                configurable: true
            });
        }
    }
}



export function PropagateEntity<T extends BaseEntity<T>>({ 
    collection
}: { collection?: {
        name: (this: T) => string;
        exportFn?: (this: T) => Partial<T>
    }[]; } = {}) {
    return function (constructor: Function) { 
      const original = constructor.prototype.persist;
      constructor.prototype.persist = async function(onSnapshot?: DBOnSnapShotFnType) {

            const result = await original.apply(this, [onSnapshot]);
            if (collection && this._databasePersist) {
                for (const c of collection) {
                    const data = c.exportFn ? c.exportFn.apply(this) : this.export();
                    await DatabaseService.instance.set(`${c.name.apply(this)}`, data, {
                        merge: true,
                        onSnapshot
                    });
                }
            }
            return result;
      }

    }
}

export class CacheBase {

    static get canonical(): string {
        throw new Error('Not implemented');
    }

    static get canonicals() {
        return {
            days: 'd',
            months: 'm',
        }
    };
}
//